import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { Trophy, ArrowUp, ArrowDown, Minus, Users, X } from "lucide-react";
import BracketGroupResults from "@/components/app/BracketGroupResults";

export default function Leaderboard() {
  const { active } = useTournament();
  const capMult = active?.config?.captain_multiplier ?? 2;
  const ppMult = active?.config?.powerplay_multiplier ?? 3;
  const survStrikesAllowed = active?.config?.survivor_strikes_allowed ?? 2;
  const isLeague = active?.format === "league";
  const [stage, setStage] = useState("");
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("main"); // main | overall | survivor | groups | bracket | clubs
  const [glb, setGlb] = useState([]);
  const [blb, setBlb] = useState([]);
  const [koLb, setKoLb] = useState([]);
  const [overall, setOverall] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [period, setPeriod] = useState("overall"); // overall | month:YYYY-MM | half:1|2
  const [periods, setPeriods] = useState({ months: [], halves: [] });
  const [liveInfo, setLiveInfo] = useState({ has_live: false, live_match_count: 0 });
  const [breakdown, setBreakdown] = useState(null); // { loading, data }
  const [bracketModal, setBracketModal] = useState(null); // { user_id, name }
  const [survivorModal, setSurvivorModal] = useState(null); // { loading, data }
  const [myGroups, setMyGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState(""); // "" = all players

  const openBreakdown = async (userId) => {
    setBreakdown({ loading: true, data: null });
    try {
      const { data } = await api.get(`/tournaments/${active.tournament_id}/players/${userId}/breakdown`);
      setBreakdown({ loading: false, data });
    } catch {
      setBreakdown({ loading: false, data: { name: "Player", total: 0, rows: [] } });
    }
  };

  const openSurvivorPicks = async (userId, name) => {
    setSurvivorModal({ loading: true, data: { name } });
    try {
      const { data } = await api.get(`/tournaments/${active.tournament_id}/survivor/picks/${userId}`);
      setSurvivorModal({ loading: false, data });
    } catch {
      setSurvivorModal({ loading: false, data: { name: name || "Player", buckets: [] } });
    }
  };

  // Load the groups this player belongs to (for the individual-board filter)
  useEffect(() => {
    if (!active) return;
    api.get(`/tournaments/${active.tournament_id}/my-groups`)
      .then(({ data }) => setMyGroups(data || []))
      .catch(() => setMyGroups([]));
    if (active.format === "league") {
      api.get(`/tournaments/${active.tournament_id}/leaderboard/periods`)
        .then(({ data }) => setPeriods(data || { months: [], halves: [] }))
        .catch(() => setPeriods({ months: [], halves: [] }));
    }
  }, [active]);

  useEffect(() => {
    if (!active) return;
    let interval;
    const load = async () => {
      if (tab === "main") {
        const gp = groupFilter ? { group_id: groupFilter } : {};
        if (isLeague && period !== "overall") {
          const [ptype, pvalue] = period.split(":");
          const { data } = await api.get(`/tournaments/${active.tournament_id}/leaderboard/period`, { params: { type: ptype, value: pvalue, ...gp } });
          setRows(data);
          setLiveInfo({ has_live: false, live_match_count: 0 });
        } else if (stage) {
          const { data } = await api.get(`/tournaments/${active.tournament_id}/leaderboard`, { params: { stage, ...gp } });
          setRows(data);
          setLiveInfo({ has_live: false, live_match_count: 0 });
        } else {
          const { data } = await api.get(`/tournaments/${active.tournament_id}/leaderboard/provisional`, { params: gp });
          setRows(data.rows || []);
          setLiveInfo({ has_live: !!data.has_live, live_match_count: data.live_match_count || 0 });
        }
      } else if (tab === "clubs") {
        const { data } = await api.get(`/tournaments/${active.tournament_id}/leaderboard/clubs`);
        setClubs(data);
      } else if (tab === "survivor") {
        const { data } = await api.get(`/tournaments/${active.tournament_id}/survivor/standings`);
        setRows(data);
      } else if (tab === "groups") {
        const { data } = await api.get(`/tournaments/${active.tournament_id}/group-leaderboard`);
        setGlb(data);
      } else if (tab === "bracket") {
        const { data } = await api.get(`/tournaments/${active.tournament_id}/bracket-leaderboard`);
        setBlb(data);
      } else if (tab === "bracket_ko") {
        const { data } = await api.get(`/tournaments/${active.tournament_id}/bracket-ko-leaderboard`);
        setKoLb(data);
      } else if (tab === "overall") {
        const { data } = await api.get(`/tournaments/${active.tournament_id}/leaderboard/overall`);
        setOverall(data);
      }
    };
    load();
    // keep the overall board fresh while games are in play
    if (tab === "main" && !stage && period === "overall") interval = setInterval(load, 30000);
    return () => { if (interval) clearInterval(interval); };
  }, [active, stage, tab, groupFilter, period, isLeague]);

  if (!active) return null;
  const showSurvivor = active.competitions?.survivor;
  const showBracket = active.competitions?.bracket;

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-tag mb-1">Standings</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase">Leaderboard</h1>
        </div>
        <div className="flex gap-1 surface-card p-1">
          <button onClick={() => setTab("main")} data-testid="lb-tab-main" className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === "main" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>Individual</button>
          {!isLeague && active.competitions?.bracket && <button onClick={() => setTab("overall")} data-testid="lb-tab-overall" className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === "overall" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>Overall</button>}
          {showSurvivor && <button onClick={() => setTab("survivor")} data-testid="lb-tab-survivor" className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === "survivor" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>Survivor</button>}
          {showBracket && <button onClick={() => setTab("bracket")} data-testid="lb-tab-bracket" className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === "bracket" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>Bracket</button>}
          {active.competitions?.bracket_ko && <button onClick={() => setTab("bracket_ko")} data-testid="lb-tab-bracket-ko" className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === "bracket_ko" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>Bracket KO</button>}
          {isLeague && <button onClick={() => setTab("clubs")} data-testid="lb-tab-clubs" className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === "clubs" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>Clubs</button>}
          <button onClick={() => setTab("groups")} data-testid="lb-tab-groups" className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === "groups" ? "bg-primary-accent text-white" : "text-zinc-400"}`}>Groups</button>
        </div>
      </div>

      {tab === "main" && (
        isLeague ? (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="label-tag">Board</span>
          <select
            data-testid="lb-period-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 text-xs uppercase tracking-widest text-white px-3 py-1.5 focus:border-primary-accent outline-none"
          >
            <option value="overall">Overall Season</option>
            {periods.halves?.map((h) => <option key={`half:${h.value}`} value={`half:${h.value}`}>{h.label}</option>)}
            {periods.months?.map((m) => <option key={`month:${m.value}`} value={`month:${m.value}`}>{m.label}</option>)}
          </select>
          {myGroups.length > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="label-tag">Group</span>
              <select data-testid="lb-group-filter" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 text-xs uppercase tracking-widest text-white px-3 py-1.5 focus:border-primary-accent outline-none">
                <option value="">All players</option>
                {myGroups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
              </select>
            </div>
          )}
        </div>
        ) : (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="label-tag">Stage</span>
          {[
            { v: "", l: "Overall" }, { v: "group", l: "Group Stage" }, { v: "r16", l: "R16" },
            { v: "qf", l: "Quarters" }, { v: "sf", l: "Semis" }, { v: "final", l: "Final" },
          ].map((s) => (
            <button key={s.v} data-testid={`stage-${s.v || "overall"}`} onClick={() => setStage(s.v)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${stage === s.v ? "border-primary-accent text-primary-accent" : "border-zinc-800 text-zinc-500 hover:text-white"}`}>
              {s.l}
            </button>
          ))}
          {myGroups.length > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="label-tag">Group</span>
              <select
                data-testid="lb-group-filter"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 text-xs uppercase tracking-widest text-white px-3 py-1.5 focus:border-primary-accent outline-none"
              >
                <option value="">All players</option>
                {myGroups.map((g) => (
                  <option key={g.group_id} value={g.group_id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        )
      )}

      {tab === "groups" && (
        <div className="text-xs text-zinc-500 mb-4 uppercase tracking-widest">
          Groups are ranked by <span className="text-white">average score</span> (total group points ÷ members).
        </div>
      )}

      {tab === "bracket" && (
        <div className="text-xs text-zinc-500 mb-4 uppercase tracking-widest">
          Bracket scores update <span className="text-white">after every settled match</span> — group finishes, best thirds, knockout winners &amp; champion. <span className="text-zinc-400">Tap a player for the group-by-group breakdown.</span>
        </div>
      )}

      {tab === "overall" && (
        <div className="text-xs text-zinc-500 mb-4 uppercase tracking-widest">
          Overall combines <span className="text-white">match prediction</span> points and <span className="text-white">bracket</span> points into one ranking.
        </div>
      )}

      {tab === "main" && !stage && liveInfo.has_live && (
        <div className="mb-3 flex items-center gap-2 px-4 py-2.5 rounded border border-green-500/40 bg-green-500/5" data-testid="lb-provisional-banner">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
          </span>
          <span className="text-xs text-green-300">
            <span className="font-display uppercase tracking-widest text-green-400">Provisional</span>
            {" "}— {liveInfo.live_match_count} game{liveInfo.live_match_count > 1 ? "s" : ""} in play. Points update live and are confirmed when matches settle.
          </span>
        </div>
      )}

      <div className="surface-card overflow-x-auto">
        {tab === "overall" ? (
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 label-tag w-16">Rank</th>
                <th className="text-left px-4 py-3 label-tag">Player</th>
                <th className="text-right px-4 py-3 label-tag">Match</th>
                <th className="text-right px-4 py-3 label-tag">Bracket</th>
                <th className="text-right px-4 py-3 label-tag">Total</th>
              </tr>
            </thead>
            <tbody>
              {overall.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-500 py-8">No data yet — settle matches to populate.</td></tr>}
              {overall.map((r) => (
                <tr key={r.user_id} onClick={() => openBreakdown(r.user_id)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" data-testid={`overall-row-${r.user_id}`} title="View match points breakdown">
                  <td className="px-4 py-3 font-display text-2xl">
                    <span className={r.rank <= 3 ? "text-primary-accent" : "text-zinc-400"}>
                      {r.rank === 1 && <Trophy size={18} className="inline mr-1"/>}{r.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-display text-lg tracking-wider underline decoration-dotted decoration-zinc-600 underline-offset-4">{r.name}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.match_points}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.bracket_points}</td>
                  <td className="px-4 py-3 text-right font-display text-2xl text-primary-accent tabular-nums" data-testid={`overall-total-${r.user_id}`}>{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === "clubs" ? (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 label-tag w-16">Rank</th>
                <th className="text-left px-4 py-3 label-tag">Club</th>
                <th className="text-right px-4 py-3 label-tag">Fans</th>
                <th className="text-right px-4 py-3 label-tag">Score</th>
              </tr>
            </thead>
            <tbody>
              {clubs.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-500 py-8">No clubs yet — pick a favourite team in your profile to start a club.</td></tr>}
              {clubs.map((c) => (
                <tr key={c.team_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`club-row-${c.team_id}`}>
                  <td className="px-4 py-3 font-display text-2xl">
                    <span className={c.rank <= 3 ? "text-primary-accent" : "text-zinc-400"}>
                      {c.rank === 1 && <Trophy size={18} className="inline mr-1"/>}{c.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {c.crest && <img src={c.crest} alt="" className="w-6 h-6 object-contain" />}
                      <span className="font-display text-lg tracking-wider">{c.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{c.members}</td>
                  <td className="px-4 py-3 text-right font-display text-2xl text-primary-accent tabular-nums" data-testid={`club-score-${c.team_id}`}>{c.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === "bracket" ? (
          <table className="w-full min-w-[680px]">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 label-tag w-16">Rank</th>
                <th className="text-left px-4 py-3 label-tag">Player</th>
                <th className="text-right px-4 py-3 label-tag">Groups</th>
                <th className="text-right px-4 py-3 label-tag">Thirds</th>
                <th className="text-right px-4 py-3 label-tag">Knockouts</th>
                <th className="text-right px-4 py-3 label-tag">Points</th>
              </tr>
            </thead>
            <tbody>
              {blb.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-8">No bracket entries yet — picks score as matches are settled.</td></tr>}
              {blb.map((r) => (
                <tr key={r.user_id} onClick={() => setBracketModal({ user_id: r.user_id, name: r.name })} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" data-testid={`blb-row-${r.user_id}`} title="View group-by-group breakdown">
                  <td className="px-4 py-3 font-display text-2xl">
                    <span className={r.rank <= 3 ? "text-primary-accent" : "text-zinc-400"}>
                      {r.rank === 1 && <Trophy size={18} className="inline mr-1"/>}
                      {r.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-display text-lg tracking-wider underline decoration-dotted decoration-zinc-600 underline-offset-4">{r.name}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.groups_pts}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.thirds_pts}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.ko_pts}</td>
                  <td className="px-4 py-3 text-right font-display text-2xl text-primary-accent" data-testid={`blb-points-${r.user_id}`}>{r.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === "bracket_ko" ? (
          <table className="w-full min-w-[680px]">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 label-tag w-16">Rank</th>
                <th className="text-left px-4 py-3 label-tag">Player</th>
                <th className="text-right px-4 py-3 label-tag">R32</th>
                <th className="text-right px-4 py-3 label-tag">R16</th>
                <th className="text-right px-4 py-3 label-tag">QF</th>
                <th className="text-right px-4 py-3 label-tag">SF</th>
                <th className="text-right px-4 py-3 label-tag">Champ</th>
                <th className="text-right px-4 py-3 label-tag">Points</th>
              </tr>
            </thead>
            <tbody>
              {koLb.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-500 py-8">No Bracket KO entries yet — picks score as knockout matches are settled.</td></tr>}
              {koLb.map((r) => (
                <tr key={r.user_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`kolb-row-${r.user_id}`}>
                  <td className="px-4 py-3 font-display text-2xl">
                    <span className={r.rank <= 3 ? "text-primary-accent" : "text-zinc-400"}>
                      {r.rank === 1 && <Trophy size={18} className="inline mr-1"/>}{r.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-display text-lg tracking-wider">{r.name}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.breakdown?.r32 || 0}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.breakdown?.r16 || 0}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.breakdown?.qf || 0}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.breakdown?.sf || 0}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 tabular-nums">{r.breakdown?.champion || 0}</td>
                  <td className="px-4 py-3 text-right font-display text-2xl text-primary-accent" data-testid={`kolb-points-${r.user_id}`}>{r.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === "groups" ? (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 label-tag w-16">Rank</th>
                <th className="text-left px-4 py-3 label-tag">Group</th>
                <th className="text-right px-4 py-3 label-tag">Members</th>
                <th className="text-right px-4 py-3 label-tag">Avg Score</th>
              </tr>
            </thead>
            <tbody>
              {glb.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-500 py-8">No group data yet.</td></tr>}
              {glb.map((g) => (
                <tr key={g.group_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`glb-row-${g.group_id}`}>
                  <td className="px-4 py-3 font-display text-2xl">
                    <span className={g.rank <= 3 ? "text-primary-accent" : "text-zinc-400"}>{g.rank}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-1 h-8 inline-block" style={{ background: g.color }}/>
                      <span className="font-display text-lg tracking-wider">{g.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-400 flex items-center justify-end gap-1"><Users size={12}/>{g.members}</td>
                  <td className="px-4 py-3 text-right font-display text-2xl text-primary-accent">{g.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === "survivor" ? (
          isLeague ? (
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 label-tag w-16">Rank</th>
                <th className="text-left px-4 py-3 label-tag">Player</th>
                <th className="text-right px-4 py-3 label-tag">Weeks Survived</th>
                <th className="text-right px-4 py-3 label-tag">Survivor Points</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-500 py-8">No survivor data yet.</td></tr>}
              {rows.map((r, i) => (
                <tr key={r.user_id} onClick={() => openSurvivorPicks(r.user_id, r.name)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" data-testid={`survivor-row-${r.user_id}`} title="View survivor picks">
                  <td className="px-4 py-3 font-display text-2xl text-primary-accent">{i + 1}</td>
                  <td className="px-4 py-3 font-display text-lg tracking-wider underline decoration-dotted decoration-zinc-600 underline-offset-4">{r.name}</td>
                  <td className="px-4 py-3 text-right text-green-400 tabular-nums font-display text-lg">{r.weeks_survived}<span className="text-zinc-600 text-sm">/{r.weeks_played}</span></td>
                  <td className="px-4 py-3 text-right font-display text-2xl text-primary-accent tabular-nums" data-testid={`survivor-points-${r.user_id}`}>{r.total_points}</td>
                </tr>
              ))}
            </tbody>
          </table>
          ) : (
          <table className="w-full min-w-[680px]">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 label-tag w-16">Rank</th>
                <th className="text-left px-4 py-3 label-tag">Player</th>
                <th className="text-right px-4 py-3 label-tag">Survived</th>
                <th className="text-left px-4 py-3 label-tag">Strikes</th>
                <th className="text-right px-4 py-3 label-tag">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-500 py-8">No survivor data yet.</td></tr>}
              {rows.map((r, i) => (
                <tr key={r.user_id} onClick={() => openSurvivorPicks(r.user_id, r.name)} className={`border-b border-white/5 hover:bg-white/5 cursor-pointer ${r.alive ? "" : "opacity-70"}`} data-testid={`survivor-row-${r.user_id}`} title="View survivor picks">
                  <td className="px-4 py-3 font-display text-2xl">
                    <span className={r.alive ? "text-primary-accent" : "text-zinc-600"}>{i + 1}</span>
                  </td>
                  <td className="px-4 py-3 font-display text-lg tracking-wider underline decoration-dotted decoration-zinc-600 underline-offset-4">{r.name}</td>
                  <td className="px-4 py-3 text-right text-green-400 tabular-nums font-display text-lg">{r.rounds}</td>
                  <td className="px-4 py-3" data-testid={`survivor-strikes-${r.user_id}`}>
                    {(r.strikes_detail && r.strikes_detail.length) ? (
                      <div className="flex flex-wrap gap-1.5">
                        {r.strikes_detail.map((s, idx) => {
                          const eliminating = !r.alive && idx === r.strikes_detail.length - 1;
                          return (
                            <span
                              key={idx}
                              title={`${s.round || ""} · ${s.team_name || s.team_code} vs ${s.opponent || s.opponent_code || "?"}${s.score ? ` (${s.score})` : ""}`}
                              className={`inline-flex items-center gap-1 px-2 py-1 border text-xs uppercase tracking-wide text-primary-accent border-primary-accent/40 bg-primary-accent/10 ${eliminating ? "ring-1 ring-primary-accent" : ""}`}
                            >
                              {s.team_crest && <img src={s.team_crest} alt="" className="w-3.5 h-3.5 object-contain" />}
                              <span className="font-display">{s.team_code || s.team_name}</span>
                              {s.score && <span className="text-zinc-500 normal-case">{s.score}</span>}
                            </span>
                          );
                        })}
                      </div>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className={`text-xs uppercase tracking-widest ${r.alive ? "text-primary-accent" : "text-zinc-500"}`} data-testid={`survivor-status-${r.user_id}`}>{r.alive ? "Alive" : "Eliminated"}</span>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{r.strikes} / {survStrikesAllowed + 1} strikes</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )
        ) : (
          <table className="w-full min-w-[680px]">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 label-tag w-16">Rank</th>
                <th className="text-left px-4 py-3 label-tag">Player</th>
                <th className="text-right px-4 py-3 label-tag">Exact</th>
                <th className="text-right px-4 py-3 label-tag">Streak</th>
                <th className="text-right px-4 py-3 label-tag">Move</th>
                <th className="text-right px-4 py-3 label-tag">Points</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-8">No data yet — settle matches to populate.</td></tr>}
              {rows.map((r) => (
                <tr key={r.user_id} onClick={() => openBreakdown(r.user_id)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" data-testid={`lb-row-${r.user_id}`} title="View points breakdown">
                  <td className="px-4 py-3 font-display text-2xl">
                    <span className={r.rank <= 3 ? "text-primary-accent" : "text-zinc-400"}>
                      {r.rank === 1 && <Trophy size={18} className="inline mr-1"/>}
                      {r.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-display text-lg tracking-wider underline decoration-dotted decoration-zinc-600 underline-offset-4">{r.name}</td>
                  <td className="px-4 py-3 text-right text-zinc-400">{r.exact_scores}</td>
                  <td className="px-4 py-3 text-right text-zinc-400">{r.best_streak}</td>
                  <td className="px-4 py-3 text-right">
                    {r.movement > 0 ? (
                      <span className="text-green-400 inline-flex items-center gap-1"><ArrowUp size={12}/>{r.movement}</span>
                    ) : r.movement < 0 ? (
                      <span className="text-primary-accent inline-flex items-center gap-1"><ArrowDown size={12}/>{Math.abs(r.movement)}</span>
                    ) : (
                      <span className="text-zinc-600 inline-flex items-center"><Minus size={12}/></span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-display text-2xl text-primary-accent" data-testid={`lb-points-${r.user_id}`}>
                    {liveInfo.has_live ? r.provisional_points : r.points}
                    {liveInfo.has_live && r.live_points > 0 && (
                      <span className="block text-[10px] font-sans uppercase tracking-widest text-green-400" data-testid={`lb-live-${r.user_id}`}>+{r.live_points} live</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {breakdown && createPortal((
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBreakdown(null)} data-testid="lb-breakdown-modal">
          <div className="surface-card max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <div className="label-tag text-zinc-500">Points Breakdown</div>
                <h3 className="font-display text-2xl uppercase">{breakdown.data?.name || "…"}</h3>
              </div>
              <div className="flex items-center gap-4">
                {breakdown.data && <div className="text-right"><div className="font-display text-3xl text-primary-accent">{breakdown.data.total}</div><div className="label-tag text-zinc-500">Total pts</div></div>}
                <button onClick={() => setBreakdown(null)} data-testid="lb-breakdown-close" className="text-zinc-400 hover:text-white"><X size={22}/></button>
              </div>
            </div>
            <div className="overflow-y-auto p-2">
              {breakdown.loading ? (
                <div className="p-8 text-center text-zinc-500">Loading…</div>
              ) : !breakdown.data?.rows?.length ? (
                <div className="p-8 text-center text-zinc-500" data-testid="lb-breakdown-empty">No settled predictions yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left px-3 py-2 label-tag">Match</th>
                      <th className="text-center px-3 py-2 label-tag">Result</th>
                      <th className="text-center px-3 py-2 label-tag">Pick</th>
                      <th className="text-left px-3 py-2 label-tag">How</th>
                      <th className="text-right px-3 py-2 label-tag">Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.data.rows.map((r) => (
                      <tr key={r.match_id} className="border-b border-white/5" data-testid={`lb-bd-row-${r.match_id}`}>
                        <td className="px-3 py-2"><span className="text-zinc-300">{r.home_code || r.home} v {r.away_code || r.away}</span></td>
                        <td className="px-3 py-2 text-center font-display tabular-nums">{r.home_score}–{r.away_score}</td>
                        <td className="px-3 py-2 text-center font-display tabular-nums text-zinc-400">{r.home_pred}–{r.away_pred}</td>
                        <td className="px-3 py-2">
                          <span className={r.tier === "Missed" ? "text-zinc-500" : "text-zinc-200"}>{r.tier}{r.tier_points > 0 ? ` +${r.tier_points}` : ""}</span>
                          {r.underdog > 0 && <span className="text-amber-400 ml-1">+{r.underdog} underdog</span>}
                          {r.penalty_bonus > 0 && <span className="text-purple-300 ml-1">+{r.penalty_bonus} pens</span>}
                          {r.is_captain && <span className="text-yellow-400 ml-1">★×{capMult}</span>}
                          {r.power_play && <span className="text-primary-accent ml-1">{ppMult}x</span>}
                        </td>
                        <td className={`px-3 py-2 text-right font-display ${r.points > 0 ? "text-primary-accent" : "text-zinc-500"}`}>{r.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ), document.body)}

      {bracketModal && createPortal((
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBracketModal(null)} data-testid="lb-bracket-modal">
          <div className="surface-card max-w-3xl w-full max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <div className="label-tag text-zinc-500">Bracket — Group Breakdown</div>
                <h3 className="font-display text-2xl uppercase">{bracketModal.name}</h3>
              </div>
              <button onClick={() => setBracketModal(null)} data-testid="lb-bracket-close" className="text-zinc-400 hover:text-white"><X size={22}/></button>
            </div>
            <div className="overflow-y-auto p-4">
              <BracketGroupResults tournamentId={active.tournament_id} userId={bracketModal.user_id} showHeader={false} />
            </div>
          </div>
        </div>
      ), document.body)}

      {survivorModal && createPortal((
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSurvivorModal(null)} data-testid="lb-survivor-modal">
          <div className="surface-card max-w-xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <div className="label-tag text-zinc-500">Survivor Picks</div>
                <h3 className="font-display text-2xl uppercase">{survivorModal.data?.name || "…"}</h3>
              </div>
              <button onClick={() => setSurvivorModal(null)} data-testid="lb-survivor-close" className="text-zinc-400 hover:text-white"><X size={22}/></button>
            </div>
            <div className="overflow-y-auto p-4 space-y-3">
              {survivorModal.loading ? (
                <div className="p-8 text-center text-zinc-500">Loading…</div>
              ) : !survivorModal.data?.buckets?.length ? (
                <div className="p-8 text-center text-zinc-500" data-testid="lb-survivor-empty">No locked picks to show yet.</div>
              ) : (
                survivorModal.data.buckets.map((b) => (
                  <div key={b.bucket_key} className="border border-white/10 bg-black/30 p-3" data-testid={`survivor-pick-bucket-${b.bucket_key}`}>
                    <div className="label-tag mb-2">{b.label}</div>
                    <div className="flex flex-wrap gap-2">
                      {b.picks.map((p, idx) => {
                        const cls = p.result === "survived" ? "border-green-500/40 bg-green-500/10 text-green-400"
                          : p.result === "strike" ? "border-primary-accent/40 bg-primary-accent/10 text-primary-accent"
                          : "border-zinc-700 bg-black/30 text-zinc-400";
                        return (
                          <span key={idx} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-xs uppercase tracking-wide ${cls}`}
                            title={`${p.team_name || p.team_code} vs ${p.opponent || p.opponent_code || "?"}${p.score ? ` (${p.score})` : ""} — ${p.result}`}>
                            {p.team_crest && <img src={p.team_crest} alt="" className="w-4 h-4 object-contain" />}
                            <span className="font-display">{p.team_code || p.team_name}</span>
                            {p.score && <span className="text-zinc-500 normal-case">{p.score}</span>}
                            <span className="opacity-70 normal-case">· {p.result}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
